import { ActionMeta } from '@databases/atlaskit/Select';
import { useFlaggingCallback } from '@databases/flags';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { BlogpostReference, PageReference } from '@databases/types/confluence';
import { DatabaseFieldType } from '@databases/types/database';
import { getContentKey } from '@databases/utils/confluence/utils';
import {
  PageLabelsDelta,
  applyPageLabels,
  getContentLabels,
  getReferencedLabelContentRefs,
} from '@databases/utils/database/field-types/pageLabels';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';
import { getLabelsDelta } from '@databases/utils/deltas/labels-delta';
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import PageLabelsPicker, { PageLabelsPickerOption } from '../../PageLabelsPicker';
import { useScope } from '../../Scope';
import { EditComponentProps } from '../fieldTypes';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;
`;

function applyLabelsDeltas(labels: string[], deltas: PageLabelsDelta[]): string[] {
  return deltas.reduce(
    (p, c) => [...new Set([...p, ...c.add].filter((l) => !c.remove.includes(l)))],
    labels
  );
}

const PageLabelsValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.PAGE_LABELS>
> = ({
  onFocus,
  onBlur,
  value,
  field,
  entryId,
  contentId,
  autoFocus,
  isInvalid,
  isSeamless,
}) => {
  const scope = useScope();
  const contentRefs = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const entry = entryId && getDatabaseEntryRequired(databaseDraft, entryId);
    return entry
      ? getReferencedLabelContentRefs({ database: databaseDraft, field, entry }, state)
      : [];
  });

  return (
    <Container>
      {contentRefs.map((contentRef, i) => {
        return (
          <li key={`${getContentKey(contentRef)}${i}`}>
            <SinglePageLabelEdit
              {...{
                contentRef,
                value,
                onFocus,
                onBlur,
                autoFocus,
                isInvalid,
                isSeamless,
              }}
            />
          </li>
        );
      })}
    </Container>
  );
};

export default PageLabelsValueEdit;

interface SinglePageLabelEditProps
  extends Pick<
    EditComponentProps<DatabaseFieldType.PAGE_LABELS>,
    'value' | 'onFocus' | 'onBlur' | 'autoFocus' | 'isInvalid' | 'isSeamless'
  > {
  contentRef: PageReference | BlogpostReference;
}

const SinglePageLabelEdit: FunctionComponent<SinglePageLabelEditProps> = ({
  contentRef,
  value,
  onFocus,
  onBlur,
  autoFocus,
  isInvalid,
  isSeamless,
}) => {
  const { formatMessage } = useIntl();

  const [labels] = defaultStoreUtils.useEqualSelector((state) => {
    return [getContentLabels(contentRef, value, state) ?? []] as const;
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [pendingDeltas, setPendingDeltas] = useState<PageLabelsDelta[]>([]);
  const resolvedLabels = useMemo(
    () => applyLabelsDeltas(labels, pendingDeltas),
    [labels, pendingDeltas]
  );

  const store = defaultStoreUtils.useStore();
  const handleChange = useFlaggingCallback(
    async (_: string[], action: ActionMeta<PageLabelsPickerOption>) => {
      const delta = getLabelsDelta(action);
      if (delta) {
        setPendingDeltas((deltas) => [...deltas, delta]);
        try {
          await applyPageLabels(contentRef, delta, store);
        } catch (e: unknown) {
          setErrorMessage(
            formatMessage(messages['field-settings.type-page-label-label-create-error'])
          );
          return;
        } finally {
          setPendingDeltas((deltas) => deltas.filter((d) => d !== delta));
        }
      }
    },
    [contentRef, formatMessage, store]
  );

  const handleFocus = useCallback(
    (e?: React.FocusEvent<HTMLElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e?: React.FocusEvent<HTMLElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  return (
    <ValidationStatus error={errorMessage} isTouched={isFocused}>
      {(props) => (
        <PageLabelsPicker
          {...props}
          onInputChange={() => setErrorMessage('')}
          value={resolvedLabels}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          isInvalid={isInvalid}
          isSeamless={isSeamless}
          isLoading={pendingDeltas.length > 0}
        />
      )}
    </ValidationStatus>
  );
};
